import { useState, useEffect } from 'react';
import { useAuth } from 'context/AuthContext';
import { MasterDocument } from '@util/firestore/master';

const SESSION_KEY = 'gf-notify-popup-dismissed';

interface MasterNotifyPopupProps {
  master: MasterDocument;
}

export default function MasterNotifyPopup({ master }: MasterNotifyPopupProps) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) return;
    if (typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY))
      return;

    const timer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(timer);
  }, [user]);

  const dismiss = () => {
    setVisible(false);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_KEY, '1');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch('/api/master/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterId: master.id,
          masterTitle: master.title,
          email,
        }),
      });

      if (!res.ok) throw new Error();

      setSubmitted(true);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SESSION_KEY, '1');
      }
      setTimeout(() => setVisible(false), 3000);
    } catch {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-[2rem] right-[2rem] left-[2rem] md:left-auto z-40 bg-white rounded-[1.2rem] shadow-[0_4px_24px_rgba(0,0,0,0.15)] p-[2rem] md:max-w-[36rem]">
      <button
        onClick={dismiss}
        className="absolute top-[1rem] right-[1.2rem] text-[1.6rem] text-gray-400 hover:text-black transition-colors cursor-pointer leading-none"
        aria-label="Close"
      >
        &times;
      </button>

      {submitted ? (
        <div className="flex items-center gap-[0.8rem]">
          <span className="text-green-600 text-[1.8rem] font-bold leading-none">
            &#10003;
          </span>
          <p className="text-[1.4rem] font-bold text-black">
            You&apos;re on the list!
          </p>
        </div>
      ) : (
        <>
          <p className="text-[1.6rem] font-extrabold text-black leading-[1.3] mb-[0.8rem] pr-[2rem]">
            Be the first to know.
          </p>
          <p className="text-[1.3rem] text-brand-dark-gray leading-[1.5] mb-[1.2rem]">
            Get an email alert the moment a {master.brand} {master.model} lists
            on GearFocus.
          </p>
          <form onSubmit={handleSubmit} className="flex gap-[0.8rem]">
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 min-w-0 text-[1.3rem] border border-gray-300 rounded-[0.8rem] px-[1rem] py-[0.6rem] focus:outline-hidden focus:ring-2 focus:ring-brand-secondary focus:border-transparent"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-black text-white font-bold rounded-[0.8rem] px-[1.6rem] py-[0.6rem] text-[1.3rem] hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
            >
              {submitting ? '...' : 'Notify Me'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
